import React, { useEffect, useMemo, useState } from "react";
import { Tag, Popover } from "antd";
import * as XLSX from "xlsx";
import ExcelUploadStageModal, {
  ExcelUploadTypeConfig,
  UploadRowBucket,
} from "../../../components/upload/shared/ExcelUploadStageModal";
import { UploadReportState } from "../../../components/upload/shared/uploadReport";
import { normsApis } from "../../../api/Norms";
import ProjectScopeSelect from "../shared/ProjectScopeSelect";
import {
  NORMS_TEMPLATE_HEADERS,
  NORMS_TEMPLATE_COL_WIDTHS,
} from "./normsTemplate";

// Thin wrapper over the shared ExcelUploadStageModal (upload → validating →
// preview → committing → done | failed). Only the norms-specific pieces live
// here: template generation, preview columns, row buckets, copy strings.

// Template is generated client-side (no blob deploy needed) so it always
// matches the backend parser: headers found by the 'Item Code' cell, blank
// Item Code cells inherit the value above (merged-cell style groups).
const generateNormsTemplate = () => {
  const headers = [...NORMS_TEMPLATE_HEADERS];
  // Wastage column is a PERCENT (2 = 2%), matching the manual form + "%" label.
  const sampleRows = [
    ["B3.1.1", "Tunnel Excavation - Class I, II & III", "Explosive - 40 mm", "Kg", 1, 1.4, 2, 110.91],
    ["", "", "Detonator - Non El - 5MTR", "No", 1, 0.52083333, 2, 75],
    ["", "", "Drill Bits 32x45mm", "No", 1, 0.00859375, 1, 3182],
    ["B3.1.2", "Tunnel Excavation - Class IV & V", "Explosive - 40 mm", "Kg", 1, 1, 2, 110.91],
    ["", "", "Shotcrete Accelerator", "Kg", 1, 0.35, 2, 45.5],
  ];
  const dataSheet = XLSX.utils.aoa_to_sheet([headers, ...sampleRows]);
  dataSheet["!cols"] = [...NORMS_TEMPLATE_COL_WIDTHS];

  const instructions = [
    ["Norms Upload — Instructions"],
    [""],
    ["Item Code", "Required. Groups materials into one norm (e.g. B3.1.1). Leave blank on continuation rows — a blank cell inherits the code above."],
    ["Item Description", "Optional norm title. Also inherited on blank continuation rows."],
    ["Material Description", "Required. Matched to the Material Master by name (case/space-insensitive). Unmatched names are auto-created as new materials."],
    ["Material Purchase Unit", "UOM name or short code (Kg, No, Meter, RM, MT, Bag, Sqm...). Unrecognized units are imported with a warning."],
    ["PUC Factor", "Purchase unit conversion factor. Optional, defaults to 1. Max 4 decimals."],
    ["Consumption Norm/Unit", "Required, > 0. Quantity consumed per unit of task work quantity. Max 8 decimals. Rows with 0 or blank are skipped (material not applicable)."],
    ["Material Wastage (%)", "Percent value: 2 = 2%, 1 = 1%, 0.5 = 0.5%. Optional, defaults to 0."],
    ["Material Unit Price", "In ₹, max 4 decimals. Optional — when blank, the price falls back to the Material Master at task-attach time."],
    [""],
    ["Norm Status", "Optional 'Active'/'Inactive' column, read from the group's first row — toggles the norm's status on upsert."],
    ["Line Status", "Optional 'Active'/'Inactive' column per material row — 'Inactive' deactivates that material's line on the norm."],
    ["Note", "Existing norm codes are UPSERTED: the title/status can change, a line with changed values is deactivated and re-added (lines are never edited in place), new materials become new lines, and lines absent from the file stay untouched. New codes are created."],
  ];
  const instructionSheet = XLSX.utils.aoa_to_sheet(instructions);
  instructionSheet["!cols"] = [{ wch: 24 }, { wch: 110 }];

  const workbook = XLSX.utils.book_new();
  // data sheet MUST be first — the backend parser reads the first worksheet
  XLSX.utils.book_append_sheet(workbook, dataSheet, "Norms");
  XLSX.utils.book_append_sheet(workbook, instructionSheet, "Instructions");
  XLSX.writeFile(workbook, "norms-upload-template.xlsx");
};

const previewColumns: any[] = [
  {
    title: "Status",
    key: "rowStatus",
    width: 100,
    fixed: "left",
    render: (_: any, record: any) => {
      const errors: string[] = Array.isArray(record?.Error)
        ? record.Error
        : record?.Error
        ? [record.Error]
        : [];
      if (errors.length) {
        return (
          <Popover
            placement="right"
            title={<span style={{ color: "red" }}>Row Errors</span>}
            content={
              <div style={{ maxWidth: 320 }}>
                {errors.map((err, i) => (
                  <div key={i} style={{ marginBottom: 4 }}>
                    • {err}
                  </div>
                ))}
              </div>
            }
          >
            <Tag color="red">Error</Tag>
          </Popover>
        );
      }
      return <Tag color="green">Valid</Tag>;
    },
  },
  {
    title: "Action",
    dataIndex: "__action",
    key: "__action",
    width: 110,
    // backward compatible: reports from before upsert have no __action
    render: (value: any) => {
      if (!value) return null;
      const color =
        value === "New Norm"
          ? "green"
          : value === "New Line"
          ? "cyan"
          : value === "Replace"
          ? "gold"
          : value === "Deactivate"
          ? "red"
          : "default"; // No Change
      return <Tag color={color}>{value}</Tag>;
    },
  },
  { title: "Excel Row", dataIndex: "excelRow", key: "excelRow", width: 90 },
  { title: "Norm Code", dataIndex: "normCode", key: "normCode", width: 110 },
  { title: "Norm Title", dataIndex: "normTitle", key: "normTitle", width: 200, ellipsis: true },
  { title: "Material", dataIndex: "materialName", key: "materialName", width: 190, ellipsis: true },
  { title: "Material Match", dataIndex: "materialStatus", key: "materialStatus", width: 130 },
  { title: "Norm/Unit", dataIndex: "consumptionNorm", key: "consumptionNorm", width: 110 },
  { title: "Wastage (%)", dataIndex: "wastagePercent", key: "wastagePercent", width: 105 },
  { title: "Unit Price (₹)", dataIndex: "unitPrice", key: "unitPrice", width: 110 },
  {
    title: "Error Details",
    key: "errorDetails",
    width: 260,
    render: (_: any, record: any) => {
      const errors: string[] = Array.isArray(record?.Error)
        ? record.Error
        : record?.Error
        ? [record.Error]
        : [];
      if (!errors.length) return null;
      return (
        <span style={{ color: "#e85347", fontSize: 12 }}>
          {errors.join("; ")}
        </span>
      );
    },
  },
];

// CRM-style row buckets. Upsert rows targeting an EXISTING norm carry
// __action 'New Line' | 'Replace' | 'Deactivate' | 'No Change' (set by the
// backend helper); 'New Norm' (or a pre-upsert report with no __action)
// counts as a plain new row. The Updates tab appears only when any exist.
const EXISTING_NORM_ACTIONS = ["New Line", "Replace", "Deactivate", "No Change"];

const bucketRows = (rows: any[]): UploadRowBucket[] => {
  const errorRows = rows.filter((row) => row?.Error);
  const updateRows = rows.filter(
    (row) => !row?.Error && EXISTING_NORM_ACTIONS.includes(row?.__action)
  );
  const validRows = rows.filter(
    (row) => !row?.Error && !EXISTING_NORM_ACTIONS.includes(row?.__action)
  );
  return [
    { key: "all", label: `All (${rows.length})`, rows },
    { key: "valid", label: `Valid (${validRows.length})`, rows: validRows },
    ...(updateRows.length
      ? [{ key: "updates", label: `Updates (${updateRows.length})`, rows: updateRows }]
      : []),
    { key: "errors", label: `Errors (${errorRows.length})`, rows: errorRows },
  ];
};

const countNormCodes = (rows: any[]): number =>
  new Set(
    rows.filter((row) => !row?.Error && row?.normCode).map((row) => row.normCode)
  ).size;

const normsUploadConfig: ExcelUploadTypeConfig = {
  title: "Bulk Norms Upload",
  entityLabel: "Norms",
  templateSource: { kind: "client", generate: generateNormsTemplate },
  startUpload: (formData) => normsApis.uploadNormsExcel(formData),
  // Overridden per-instance below to carry the active projectId scope.
  buildBody: (comments) => ({ comment: comments ? comments : "N/A" }),
  previewColumns,
  bucketRows,
  summaryTags: (report: UploadReportState) => (
    <Tag color="blue">{countNormCodes(report.rows)} norm code(s)</Tag>
  ),
  rowErrorLabel: (row) =>
    row.excelRow ? `Row ${row.excelRow}` : row.normCode || "Row",
  copy: {
    uploadIntro: (
      <p>
        You're about to import Norms. Fill the norms Excel file (one norm per
        Item Code group, one material per row) accurately and completely, then
        upload it below. You'll see a full preview before anything is saved.
      </p>
    ),
    validatingHeading: "Validating your file…",
    validatingSub:
      "Checking rows, matching materials and UOMs. This normally takes a few seconds.",
    committingHeading: "Creating norms…",
    committingSub:
      "Inserting norm codes and material lines. This normally takes a few seconds.",
    commitCta: "Create Norms",
    doneHeading: "Norms created successfully",
    doneSub: (report: UploadReportState) => (
      <>
        {countNormCodes(report.rows) || ""} norm code(s), {report.successCount}{" "}
        material line(s) imported. They are now visible in the Norms list.
      </>
    ),
    creationFailedNote:
      "Creation failed — no norms were saved (the batch is all-or-nothing).",
  },
  discardOnCancelFromPreview: false,
};

const NormUploadModal = ({
  open,
  onClose,
  onSuccess,
  projectId = 0,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Scope the uploaded norms to this projectId (0 = org master). */
  projectId?: number;
}) => {
  // The upload targets a project, defaulted from the list's current selection
  // but confirmable/changeable here so the upload is self-contained.
  const [scopeProjectId, setScopeProjectId] = useState<number>(projectId);

  useEffect(() => {
    if (open) setScopeProjectId(projectId);
  }, [open, projectId]);

  const config = useMemo<ExcelUploadTypeConfig>(
    () => ({
      ...normsUploadConfig,
      buildBody: (comments: string) => ({
        comment: comments ? comments : "N/A",
        projectId: scopeProjectId,
      }),
      introSection: (
        <div className="mt-3">
          <label className="form-label mb-1">Upload into</label>
          <div>
            <ProjectScopeSelect value={scopeProjectId} onChange={setScopeProjectId} />
          </div>
          <div className="text-soft" style={{ fontSize: 12, marginTop: 4 }}>
            Uploaded norms are saved under this project.
          </div>
        </div>
      ),
    }),
    [scopeProjectId]
  );
  return (
    <ExcelUploadStageModal
      config={config}
      open={open}
      onClose={onClose}
      onSuccess={onSuccess}
      projectId={scopeProjectId}
    />
  );
};

export default NormUploadModal;
